import { useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { analytics } from '@silvertours/front-entities';
import { http, Heading2, Ui } from '@silvertours/front-shared';
import { NewsletterRegistration as NewsletterRegistrationProps } from '@silvertours/common-landingpages-view';
import { NewsletterSignup } from './NewsletterSignup';
import { NewsletterSignupSuccess } from './NewsletterSignupSuccess';
import { useNewsletter } from './useNewsletter';
import {
  Card,
  NewsletterPictogramWrapper,
  NewsletterContainer,
  BadgeWrapper,
  StyledTooltipBadge,
  NewsletterText,
  List,
  ListItem,
  StyledChecked,
  CardWrapper,
  Container,
} from './NewsletterRegistration.styles';

const Background = ({ children }: React.PropsWithChildren<{}>) => (
  <Ui.Wave type="scalable3" fullWidth backgroundStyle="orangeAlt">
    {children}
  </Ui.Wave>
);

const NewsletterRegistration = ({
  type,
  badgeText,
  heading,
  cashbackBadgeText,
  cashbackTooltipText,
  image,
  usps,
}: NewsletterRegistrationProps) => {
  const { formatApiUrl } = useNewsletter();
  const [success, setSuccess] = useState(false);
  const isIndexType = type === 'index';

  const t = useTranslations(
    'features.newsletterRegistrationLegacy.newsletterRegistration',
  );

  const trackRegistration = () => {
    if (type === 'index') {
      analytics.gtm.trackNewsletterRegistrationIndex();
    } else {
      analytics.gtm.trackNewsletterRegistration();
    }
  };

  const onSubmit = async (email: string) => {
    const sourceParam = {
      index: t('sourceParam.index'),
      landing: t('sourceParam.landing'),
    };

    try {
      await http.axiosInstance.post(formatApiUrl('v1/newsletter/subscribe'), {
        email,
        sourceParam: sourceParam[type],
      });

      setSuccess(true);
      trackRegistration();
    } catch (axiosError: unknown) {
      // TODO: error handling (if necessary?)
    }
  };

  const newsletterForm = () =>
    success ? (
      <NewsletterSignupSuccess showTitle={isIndexType} />
    ) : (
      <NewsletterSignup truncateLegalText={isIndexType} onSubmit={onSubmit} />
    );

  if (!isIndexType) {
    return <Container>{newsletterForm()}</Container>;
  }

  const markdownToHtml = (text: string) => {
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const boldRegex = /(\*\*|__)([^*_]+)\1/g;

    return `<p>${text
      .replace(markdownLinkRegex, '<a href="$2">$1</a>')
      .replace(boldRegex, '<strong>$2</strong>')
      .replace(/\r?\n/g, '<br />')}</p>`;
  };

  return (
    <Background>
      <NewsletterContainer>
        <NewsletterText>
          <Ui.SectionTitle
            text={badgeText ?? 'Newsletter'}
            type="brand-orange"
          />
          <Heading2>{heading ?? t('title')}</Heading2>
          <List>
            {usps &&
              usps.map(item => (
                <ListItem key={item}>
                  <StyledChecked /> {item}
                </ListItem>
              ))}
          </List>
        </NewsletterText>
        <CardWrapper>
          {image && (
            <NewsletterPictogramWrapper>
              <Image
                {...image}
                alt=""
                width="200"
                style={{ objectFit: 'contain' }}
              />
            </NewsletterPictogramWrapper>
          )}
          <Card>
            <BadgeWrapper>
              <StyledTooltipBadge
                badgeText={cashbackBadgeText ?? t('discount.badge')}
                tooltipTitle={t('discount.tooltip.title')}
                tooltipText={
                  cashbackTooltipText
                    ? markdownToHtml(cashbackTooltipText)
                    : t.raw('discount.tooltip.text')
                }
                tooltipLabel={t('discount.tooltip.label')}
                onHover={() => analytics.gtm.trackCancellationHover()}
              />
            </BadgeWrapper>
            {newsletterForm()}
          </Card>
        </CardWrapper>
      </NewsletterContainer>
    </Background>
  );
};

export { NewsletterRegistration };
